package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.RevisitCadence
import fyi.moko.hotpl.domain.model.RevisitWindow
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Computes the [RevisitWindow] for a place from its cadence and visit history.
 *
 * The cadence→threshold mapping is the single source of truth for "when is it a
 * good time to revisit this place?", so the rule can be judged in isolation
 * before any UI depends on it. Pure and deterministic: same inputs + [Clock]
 * → same output. The [Clock] is injected so tests can control "now" instead of
 * hanging on wall-clock time (this codebase has a history of infinite
 * collect loops and runTest hangs from time-dependent code).
 *
 * Cadence windows (days since the last visit):
 * - [RevisitCadence.ANYTIME] — no window. Always good (if not over-visited),
 *   [RevisitWindow.daysUntilWindow] is null.
 * - [RevisitCadence.OCCASIONAL] — 6–8 weeks (42–56 days). The "가벼운 재방문"
 *   cadence from §3.5: cafes, casual restaurants, dessert spots.
 * - [RevisitCadence.SPECIAL] — ~100 days. The "특별한 재방문" cadence: nice
 *   restaurants, bars, date spots.
 * - [RevisitCadence.ANNUAL] — ~365 days. The "계절·기념 재방문" cadence:
 *   birthdays, trips, seasonal memory places.
 *
 * Over-visit guardrail ("경고보다 맥락", §1.3 #7): a place visited within the
 * last [RECENT_VISIT_EXCLUSION_DAYS] (14 days) is never a good window now,
 * regardless of cadence. The home card quietly omits it; no warning is shown.
 *
 * This is the *window* calculation, not the *milestone* recall (that's
 * [fyi.moko.hotpl.ui.util.todaysRecalls] / recallLine). The two coexist on the
 * home screen: milestone recall surfaces "7 weeks ago today / 100 days ago
 * today / a year ago today", while the revisit window surfaces "it's a good
 * time to go back" for places whose cadence window is open today.
 */
class RevisitWindowCalculator(
    private val clock: Clock = Clock.System,
    private val zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    companion object {
        const val RECENT_VISIT_EXCLUSION_DAYS = 14

        const val OCCASIONAL_WINDOW_START_DAYS = 42
        const val OCCASIONAL_WINDOW_END_DAYS = 56

        const val SPECIAL_WINDOW_START_DAYS = 100
        const val SPECIAL_WINDOW_END_DAYS = 120

        const val ANNUAL_WINDOW_START_DAYS = 350
        const val ANNUAL_WINDOW_END_DAYS = 380
    }

    fun calculate(
        cadence: RevisitCadence,
        lastVisitedAt: Long?,
        visitCount: Int,
        nowMs: Long = clock.now().toEpochMilliseconds(),
    ): RevisitWindow {
        if (lastVisitedAt == null) {
            return RevisitWindow(
                isGoodNow = false,
                daysUntilWindow = null,
                reason = "no_visit",
            )
        }

        val today = Instant.fromEpochMilliseconds(nowMs).toLocalDateTime(zone).date
        val lastVisitDate = Instant.fromEpochMilliseconds(lastVisitedAt).toLocalDateTime(zone).date
        val daysSinceLastVisit = lastVisitDate.daysUntil(today)

        if (daysSinceLastVisit < 0) {
            return RevisitWindow(
                isGoodNow = false,
                daysUntilWindow = null,
                reason = "future_visit",
            )
        }

        if (daysSinceLastVisit <= RECENT_VISIT_EXCLUSION_DAYS) {
            val start = windowStartDays(cadence)
            return RevisitWindow(
                isGoodNow = false,
                daysUntilWindow = start?.minus(daysSinceLastVisit),
                reason = "recent_visit_excluded",
            )
        }

        val windowStart = windowStartDays(cadence)
        val windowEnd = windowEndDays(cadence)

        if (windowStart == null) {
            return RevisitWindow(
                isGoodNow = true,
                daysUntilWindow = null,
                reason = "anytime",
            )
        }

        val daysUntilWindow = windowStart - daysSinceLastVisit
        return when {
            daysSinceLastVisit in windowStart..windowEnd -> RevisitWindow(
                isGoodNow = true,
                daysUntilWindow = 0,
                reason = "in_window",
            )
            daysSinceLastVisit < windowStart -> RevisitWindow(
                isGoodNow = false,
                daysUntilWindow = daysUntilWindow,
                reason = "before_window",
            )
            else -> RevisitWindow(
                isGoodNow = true,
                daysUntilWindow = -(daysSinceLastVisit - windowEnd),
                reason = "past_window",
            )
        }
    }

    private fun windowStartDays(cadence: RevisitCadence): Int? = when (cadence) {
        RevisitCadence.ANYTIME -> null
        RevisitCadence.OCCASIONAL -> OCCASIONAL_WINDOW_START_DAYS
        RevisitCadence.SPECIAL -> SPECIAL_WINDOW_START_DAYS
        RevisitCadence.ANNUAL -> ANNUAL_WINDOW_START_DAYS
    }

    private fun windowEndDays(cadence: RevisitCadence): Int = when (cadence) {
        RevisitCadence.ANYTIME -> Int.MAX_VALUE
        RevisitCadence.OCCASIONAL -> OCCASIONAL_WINDOW_END_DAYS
        RevisitCadence.SPECIAL -> SPECIAL_WINDOW_END_DAYS
        RevisitCadence.ANNUAL -> ANNUAL_WINDOW_END_DAYS
    }
}